import { useEffect, useState } from "react";

// Layout override types
enum LayoutOverride {
  NONE = "none",
  DESKTOP = "desktop",
  MOBILE = "mobile",
}

// Global override state
let layoutOverride: LayoutOverride = LayoutOverride.NONE;
const overrideListeners: (() => void)[] = [];

export function setGlobalDesktopOverride(enabled: boolean) {
  layoutOverride = enabled ? LayoutOverride.DESKTOP : LayoutOverride.NONE;
  overrideListeners.forEach((listener) => listener());
}

export function useIsMobile() {
  const getOverriddenValue = () => {
    if (layoutOverride === LayoutOverride.MOBILE) return true;
    if (layoutOverride === LayoutOverride.DESKTOP) return false;
    return isMobileDevice();
  };

  const [isMobile, setIsMobile] = useState(() => getOverriddenValue());

  useEffect(() => {
    const onResize = () => setIsMobile(getOverriddenValue());
    window.addEventListener("resize", onResize);

    const onOverrideChange = () => {
      setIsMobile(getOverriddenValue());
    };
    overrideListeners.push(onOverrideChange);

    return () => {
      window.removeEventListener("resize", onResize);
      const index = overrideListeners.indexOf(onOverrideChange);
      if (index > -1) {
        overrideListeners.splice(index, 1);
      }
    };
  }, []);

  return isMobile;
}

function isMobileDevice(): boolean {
  const widthBreakpoint = 768;

  // SSR guard
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = (navigator as any).platform || "";
  const maxTouchPoints = (navigator as any).maxTouchPoints || 0;

  // Phones
  const isIPhone = /\biPhone\b/i.test(ua);
  const isIPod = /\biPod\b/i.test(ua);
  const isAndroid = /\bAndroid\b/i.test(ua);
  const isAndroidPhone = isAndroid && /\bMobile\b/i.test(ua);

  // iPad detection (covers iPadOS 13+ which spoofs desktop Safari)
  const isIPadUA = /\biPad\b/i.test(ua);
  const isIPadOS13Plus = !isIPadUA && /MacIntel/i.test(platform) && maxTouchPoints > 1;
  const isIPad = isIPadUA || isIPadOS13Plus;

  // Android tablet = Android without "Mobile" token
  const isAndroidTablet = isAndroid && !/\bMobile\b/i.test(ua);

  // Chromium hint (phones only; tablets usually report false)
  const uaDataMobile = (navigator as any).userAgentData?.mobile === true;

  const isPhone = isIPhone || isIPod || isAndroidPhone || uaDataMobile;
  const isTablet = isIPad || isAndroidTablet;

  // Final decision
  if (isPhone) return true;
  if (isTablet) return true;

  // Fallback: if UA is inconclusive, use viewport (prevents
  // Windows/mac touch laptops from being misclassified).
  const smallViewport = Math.max(window.innerWidth, window.innerHeight) <= widthBreakpoint;
  if (smallViewport) return true;

  return false;
}
